import {PriceStrategyService} from "../../../components/inputs/price/service/PriceStrategyService";
import {CurrentUser} from "../../../services/user/models/CurrentUser";
import {
    QuoteViewModel,
    TmcQuoteViewModel,
    UnderlyingQuoteViewModel,
    WarrantQuoteViewModel
} from "../../../quote/models/QuoteViewModel";
import {isOptionOrder, isWarrantOrder, OrderDestination, OrderSide, OrderState} from "../../models/OrderRecord";
import {CurrentUserService} from "../../../services/user/CurrentUserService";
import {
    ConditionalIressEquityOrderViewModel,
    ConditionalIressOptionOrderViewModel,
    ConditionalIressTmcOrderViewModel,
    ConditionalIressWarrantOrderViewModel,
    IressEquityOrderViewModel,
    IressOptionOrderViewModel,
    IressOrderViewModel,
    IressOrderViewModels,
    IressTmcOrderViewModel,
    IressWarrantOrderViewModel
} from "../../models/iress/IressOrderViewModel";
import {Observable} from "rxjs";
import {OptionContractsCache} from "../../../options/contracts/cache/OptionContractsCache";
import {flatMap, isNotNull} from "../../../js/util/UtilFunctions";
import {AccountBriefRecord} from "../../../account/types/AccountBriefRecord";
import {OptionRecord} from "../../../options/contracts/models/OptionChainRecord";
import {IressOrderRecord, IressOrderRecords, isIressTmcOrder} from "../../models/iress/IressOrderRecord";
import {TmcLegViewModelFactory} from "./TmcLegViewModelFactory";
import {PortfolioType} from "../../../portfolio/models/Portfolio";
import {PlacedBrokerOrders} from "../placed/models/PlacedOrder";
import {QuoteHashMap} from "../../../quote/util/QuoteStoreUtil";
import {findChildDestinationOrders, orderIsMine} from "./util/OrderViewStoreUtil";
import {QuoteParameters, TmcQuoteParameters} from "../../../quote/models/QuoteParameters";
import {isNotEmpty} from "../../models/OrderConditions";

export class IressOrderViewModelFactory {

    public static readonly id = "iressOrderViewModelFactory";

    /** @ngInject */
    public constructor(private readonly priceStrategyService: PriceStrategyService,
                       private readonly currentUserService: CurrentUserService,
                       private readonly optionContractsCache: OptionContractsCache,
                       private readonly tmcLegViewModelFactory: TmcLegViewModelFactory) {
    }

    public build(orders: IressOrderRecords,
                 quotes: QuoteHashMap<QuoteViewModel>,
                 accounts: Map<string, AccountBriefRecord>,
                 placed: PlacedBrokerOrders): Observable<IressOrderViewModels> {
        return this.currentUserService
                   .current
                   .first()
                   .flatMap((user) => this.buildModels(user, orders, quotes, accounts, placed));
    }

    private async buildModels(user: CurrentUser,
                              orders: IressOrderRecords,
                              quotes: QuoteHashMap<QuoteViewModel>,
                              accounts: Map<string, AccountBriefRecord>,
                              placed: PlacedBrokerOrders): Promise<IressOrderViewModels> {

        const orderResults = orders.map((o) => this.tryBuildOrderModels(o, quotes, accounts, user, placed));

        const models = await Promise.all(orderResults);
        return models.filter(isNotNull)
                     .reduce(flatMap, []);
    }

    private async tryBuildOrderModels(order: IressOrderRecord,
                                      quotes: QuoteHashMap<QuoteViewModel>,
                                      accounts: Map<string, AccountBriefRecord>,
                                      user: CurrentUser,
                                      placed: PlacedBrokerOrders): Promise<IressOrderViewModels | null> {
        try {
            const account = accounts.get(order.accountNumber) || null;
            return this.buildOrderModels(order, quotes, account, user, placed);
        } catch (e) {
            console.error(e);
            return null;
        }
    }

    private async buildOrderModels(order: IressOrderRecord,
                                   quotes: QuoteHashMap<QuoteViewModel>,
                                   account: AccountBriefRecord | null,
                                   user: CurrentUser,
                                   placed: PlacedBrokerOrders): Promise<IressOrderViewModels> {
        const isMine = orderIsMine(order, placed);
        const isInternal = order.isInternal;
        const orders = this.extractDestinationOrders(order);
        const results = orders.map((o) => this.tryBuildOrderModel(o, isMine, isInternal, quotes, account, user));
        const models = await Promise.all(results);
        return models.filter(isNotNull);
    }

    private extractDestinationOrders(order: IressOrderRecord): IressOrderRecords {
        if (order.destination === OrderDestination.FIXED_CO && (order.childOrders.length > 0)) {
            return findChildDestinationOrders(order);
        } else {
            return [order];
        }
    }

    private async tryBuildOrderModel(order: IressOrderRecord,
                                     isMine: boolean,
                                     isInternal: boolean,
                                     quotes: QuoteHashMap<QuoteViewModel>,
                                     account: AccountBriefRecord | null,
                                     user: CurrentUser): Promise<IressOrderViewModel | null> {
        try {
            return this.buildOrderModel(order, isMine, isInternal, quotes, account, user);
        } catch (e) {
            console.error(e);
            return null;
        }
    }

    private async buildOrderModel(order: IressOrderRecord,
                                  isMine: boolean,
                                  isInternal: boolean,
                                  quotes: QuoteHashMap<QuoteViewModel>,
                                  account: AccountBriefRecord | null,
                                  user: CurrentUser): Promise<IressOrderViewModel> {
        const isAmendable = (order.state === OrderState.ACTIVE) && user.ordersEnabled;
        const strategy = this.priceStrategyService.forCode(order.code);
        const conditions = order.conditions;
        const key = this.quoteKey(order);
        const quote = quotes.get(key);
        if (isIressTmcOrder(order)) {
            const tmcQuote = quote as TmcQuoteViewModel;
            const legs = await this.tmcLegViewModelFactory.build(order, quotes, PortfolioType.BROKER);
            if ((conditions != null) && isNotEmpty(conditions)) {
                return new ConditionalIressTmcOrderViewModel(order,
                                                             isAmendable,
                                                             tmcQuote,
                                                             strategy,
                                                             account,
                                                             isMine,
                                                             isInternal,
                                                             legs,
                                                             conditions);
            } else {
                return new IressTmcOrderViewModel(order,
                                                  isAmendable,
                                                  tmcQuote,
                                                  strategy,
                                                  account,
                                                  isMine,
                                                  isInternal,
                                                  legs);
            }
        } else if (isOptionOrder(order)) {
            let contract: OptionRecord | null = null;
            try {
                contract = await this.optionContractsCache.find(order);
            } catch (e) {
                // Do nothing
            }
            const optionQuote = quote as TmcQuoteViewModel;
            if ((conditions != null) && isNotEmpty(conditions)) {
                return new ConditionalIressOptionOrderViewModel(order,
                                                                isAmendable,
                                                                optionQuote,
                                                                strategy,
                                                                account,
                                                                isMine,
                                                                isInternal,
                                                                contract,
                                                                conditions);
            } else {
                return new IressOptionOrderViewModel(order,
                                                     isAmendable,
                                                     optionQuote,
                                                     strategy,
                                                     account,
                                                     isMine,
                                                     isInternal,
                                                     contract);
            }
        } else if (isWarrantOrder(order)) {
            const warrantQuote = quote as WarrantQuoteViewModel;
            if ((conditions != null) && isNotEmpty(conditions)) {
                return new ConditionalIressWarrantOrderViewModel(order,
                                                                 isAmendable,
                                                                 warrantQuote,
                                                                 strategy,
                                                                 account,
                                                                 isMine,
                                                                 isInternal,
                                                                 conditions);
            } else {
                return new IressWarrantOrderViewModel(order,
                                                      isAmendable,
                                                      warrantQuote,
                                                      strategy,
                                                      account,
                                                      isMine,
                                                      isInternal);
            }
        } else {
            const underlyingQuote = quote as UnderlyingQuoteViewModel;
            if ((conditions != null) && isNotEmpty(conditions)) {
                return new ConditionalIressEquityOrderViewModel(order,
                                                                isAmendable,
                                                                underlyingQuote,
                                                                strategy,
                                                                account,
                                                                isMine,
                                                                isInternal,
                                                                conditions);
            } else {
                return new IressEquityOrderViewModel(order,
                                                     isAmendable,
                                                     underlyingQuote,
                                                     strategy,
                                                     account,
                                                     isMine,
                                                     isInternal);
            }
        }
    }

    private quoteKey(order: IressOrderRecord): QuoteParameters {
        if (isIressTmcOrder(order)) {
            return {
                code: order.code,
                exchange: order.exchange,
                portfolioType: PortfolioType.BROKER,
                opposite: order.side === OrderSide.MULTI_OPPOSITE
            } as TmcQuoteParameters;
        } else {
            return {
                code: order.code,
                exchange: order.exchange,
                portfolioType: PortfolioType.BROKER
            };
        }
    }
}
